/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../../../AppContext";
import { getEventScope, isCurrentScope } from "../../../../eventIdentity";
import { useLoading } from "../../../../Components/Common/LoadingSpinner/LoadingContext";
import {
  FACE_STORE_NAME,
  getImageBlobFromDB,
} from "../../../../IndexedDBUtils";
import { useKeenSlider } from "keen-slider/react";
import { base64ToBlob, showAlert } from "../../../../generalUtils";

// Import components
import Thumbnails from "./components/Thumbnails";
import BodySlider from "./components/BodySlider";
import NavigationArrows from "./components/NavigationArrows";

// Import hooks
import useBodyImages from "./hooks/useBodyImages";
import useFaceAnimation from "./hooks/useFaceAnimation";

// Import utils
import {
  extractFaceInfoFromPath,
  calculateFaceStyle,
} from "../../../../Components/Common/FacePositionUtils/faceUtils";

// Import styles
import FlowCountdown from "../../shared/FlowCountdown";

import "keen-slider/keen-slider.min.css";
import "./MinimoveBodyGallery.css";
import "./Animations.css";

// Configuration constants
const SLIDER_ANIMATION_DURATION = 350;

const MinimoveBodyGalleryPage = () => {
  const { showLoading, hideLoading } = useLoading();
  const { eventName } = useAppContext();
  const navigate = useNavigate();

  // Ref for the entire slider container
  const sliderContainerRef = useRef(null);

  // State variables for images and slides
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedFace, setSelectedFace] = useState(null);
  const sliderDimensionsRef = useRef({ width: 0, height: 0 });
  const isAnimatingRef = useRef(false);

  // Refs for each body image's <img> element
  const imageRefsRef = useRef([]);
  // Refs for each container <div> wrapping the image (for relative positioning)
  const containerRefsRef = useRef([]);

  // Custom hooks for face animations and image loading
  const {
    showSpecialEffect,
    showFaces,
    selectedEffect,
    initialLoad,
    setInitialLoad,
    setShowSpecialEffect,
    setShowFaces,
    setSelectedEffect,
    getRandomFaceEffect,
    setFacesReady,
    carouselReady,
    setCarouselReady,
    pageReady,
    setPageReady,
  } = useFaceAnimation();

  const { images, setImages, imagesLoaded } = useBodyImages(eventName);

  // Initialize refs arrays when images change.
  useEffect(() => {
    imageRefsRef.current = images.map(
      (_, i) => imageRefsRef.current[i] || React.createRef()
    );
    containerRefsRef.current = images.map(
      (_, i) => containerRefsRef.current[i] || React.createRef()
    );
  }, [images]);

  // Handle slide change events from the slider.
  const handleSlideChange = (s) => {
    try {
      const idx = s.track.details.rel;
      isAnimatingRef.current = true;
      setCurrentSlide(idx);
    } catch (err) {
      console.error("Error in handleSlideChange:", err);
    }
  };

  // Handle slide change completion events.
  const handleSlideChangeComplete = () => {
    isAnimatingRef.current = false;
  };

  // Clean up URL resources when component unmounts or face changes
  useEffect(() => {
    return () => {
      if (selectedFace?.url) {
        URL.revokeObjectURL(selectedFace.url);
      }
    };
  }, [selectedFace]);

  // Initialize the slider.
  const [sliderRef, sliderInstance] = useKeenSlider({
    loop: true,
    mode: "free-snap",
    rubberband: false,
    slides: {
      perView: 1.3,
      spacing: 1,
      origin: "center",
    },
    dragSpeed: 1.2,
    defaultAnimation: {
      duration: SLIDER_ANIMATION_DURATION,
    },
    created: () => {
      setCarouselReady(false);
      setTimeout(() => {
        if (sliderContainerRef.current) {
          sliderDimensionsRef.current = {
            width: sliderContainerRef.current.offsetWidth,
            height: sliderContainerRef.current.offsetHeight,
          };
        }
        setCarouselReady(true);
      }, 300);
    },
    slideChanged: handleSlideChange,
    animationEnded: handleSlideChangeComplete,
  });

  // Load selected face from previous page.
  useEffect(() => {
    // Everything after an await here (navigation, the shared loading overlay)
    // only happens while this page is mounted AND still in the event visit it
    // was opened for: a late IndexedDB/image completion from event A must not
    // send B's guest back to A's face gallery or hide B's loading.
    const scope = getEventScope();
    let cancelled = false;
    const isCurrent = () => !cancelled && isCurrentScope(scope);
    const navigateIfCurrent = (to) => {
      if (isCurrent()) navigate(to);
    };
    const loadSelectedFace = async () => {
      try {
        showLoading();
        const selectedFacesJson = sessionStorage.getItem("selectedFaces");

        if (!selectedFacesJson) {
          console.warn("No selected faces found");
          showAlert("err", "לא נמצאו פנים שנבחרו, אנא חזור ובחר שנית", 3000);
          navigateIfCurrent(`/${eventName}/face-gallery`);
          return;
        }

        const selectedFaces = JSON.parse(selectedFacesJson);
        if (!selectedFaces || !selectedFaces.length) {
          console.warn("Empty selected faces array");
          showAlert("err", "בחירת הפנים ריקה, אנא חזור ובחר שנית", 3000);
          navigateIfCurrent(`/${eventName}/face-gallery`);
          return;
        }

        const facePath = selectedFaces[0];

        // Get face info from path
        const faceInfo = extractFaceInfoFromPath(facePath);

        // Load from IndexedDB
        const faceData = await getImageBlobFromDB(
          eventName,
          FACE_STORE_NAME,
          facePath
        );
        if (!isCurrent()) return;

        if (!faceData) {
          console.error("Failed to load face from IndexedDB");
          showAlert(
            "err",
            "שגיאה בטעינת הפנים הנבחרים, אנא חזור ובחר שנית",
            3000
          );
          navigateIfCurrent(`/${eventName}/face-gallery`);
          return;
        }

        // Ensure we have a blob
        const faceBlob =
          typeof faceData === "string"
            ? await base64ToBlob(faceData, "image/webp")
            : faceData;
        if (!isCurrent()) return;

        // Create URL for display
        const faceUrl = URL.createObjectURL(faceBlob);

        // Load image to get dimensions
        await new Promise((resolve, reject) => {
          const img = new Image();

          img.onload = () => {
            if (!isCurrent()) {
              URL.revokeObjectURL(faceUrl);
              resolve();
              return;
            }
            setSelectedFace({
              url: faceUrl,
              faceHeightRatio: faceInfo.faceHeightRatio,
              centerX: faceInfo.centerX,
              centerY: faceInfo.centerY,
              naturalWidth: img.naturalWidth,
              naturalHeight: img.naturalHeight,
            });
            resolve();
          };

          img.onerror = () => {
            console.error("Error loading face image");
            URL.revokeObjectURL(faceUrl);
            reject(new Error("Face image load failed"));
          };

          img.src = faceUrl;
        });

        if (isCurrent()) hideLoading();
      } catch (error) {
        console.error("Error loading selected face:", error);
        if (!isCurrent()) return;
        showAlert("err", "שגיאה בטעינת הפנים הנבחרים", 3000);
        navigateIfCurrent(`/${eventName}/face-gallery`);
      }
    };

    loadSelectedFace();
    return () => {
      cancelled = true;
    };
  }, []);

  // Manage loading state and spinner.
  useEffect(() => {
    showLoading();
    if (pageReady) {
      hideLoading();
    }
    return () => {
      hideLoading();
    };
  }, [pageReady]);

  // Synchronize content loading and face positioning.
  useEffect(() => {
    if (selectedFace && images.length > 0 && imagesLoaded && carouselReady) {
      const prepareContent = async () => {
        setFacesReady(true);
        await new Promise((resolve) => setTimeout(resolve, 300));
        setPageReady(true);
        if (initialLoad) {
          // On initial load, use a random effect (can be changed later via dropdown)
          setSelectedEffect(getRandomFaceEffect());
          setTimeout(() => {
            setShowFaces(true);
            setShowSpecialEffect(true);
            setInitialLoad(false);
          }, 300);
        } else {
          setTimeout(() => {
            setShowFaces(true);
          }, 200);
        }
      };
      prepareContent();
    }
  }, [
    selectedFace,
    images,
    imagesLoaded,
    carouselReady,
    initialLoad,
    setFacesReady,
    setPageReady,
    setShowFaces,
    setShowSpecialEffect,
    setInitialLoad,
    setSelectedEffect,
    getRandomFaceEffect,
  ]);

  // Update slider when images have loaded.
  useEffect(() => {
    if (images.length > 0 && sliderInstance && sliderInstance.current) {
      setCarouselReady(false);
      setTimeout(() => {
        sliderInstance.current.update();
        setTimeout(() => {
          setCarouselReady(true);
        }, 300);
      }, 300);
    }
  }, [images.length, sliderInstance, setCarouselReady]);

  // Listen for window resize to update face positions.
  useEffect(() => {
    const handleResize = () => {
      setImages((prevImages) => [...prevImages]);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [setImages]);

  // Generic navigation handler for slider.
  const navigateToSlide = (action) => {
    if (!sliderInstance?.current || isAnimatingRef.current) return;
    isAnimatingRef.current = true;
    if (typeof action === "number") {
      sliderInstance.current.moveToIdx(action);
    } else if (action === "next") {
      sliderInstance.current.next();
    } else if (action === "prev") {
      sliderInstance.current.prev();
    }
  };

  const handleThumbClick = (idx) => {
    navigateToSlide(idx);
  };

  const handleFinish = () => {
    if (images.length > 0) {
      const chosenBodyPath = images[currentSlide].path;
      sessionStorage.setItem(
        "selectedBodies",
        JSON.stringify([chosenBodyPath])
      );
    }
    navigate(`/${eventName}/result`);
  };

  const handleBack = () => {
    navigate(`/${eventName}/face-gallery`);
  };

  if (imagesLoaded && images.length === 0) {
    return (
      <div className="minimove-bodygallery-container no-scroll">
        <h1 className="minimove-bodygallery-title rtl">אין דמויות זמינות</h1>
        <FlowCountdown />
        <div className="minimove-bodygallery-buttons">
          <button onClick={handleBack}>חזרה</button>
        </div>
      </div>
    );
  }

  return (
    <div className="minimove-bodygallery-container no-scroll">
      <h1 className="minimove-bodygallery-title rtl">
        בחרו את הדמות שתרצו <br /> לMiniMove שלכם
      </h1>
      <FlowCountdown />

      {/* Thumbnails row */}
      <Thumbnails
        images={images}
        currentSlide={currentSlide}
        onThumbClick={handleThumbClick}
      />

      {/* Main carousel area */}
      <div
        className="minimove-bodygallery-slider-container"
        ref={sliderContainerRef}
      >
        <NavigationArrows
          onPrevClick={() => navigateToSlide("prev")}
          onNextClick={() => navigateToSlide("next")}
        />

        <BodySlider
          sliderRef={sliderRef}
          images={images}
          imageRefs={imageRefsRef.current}
          containerRefs={containerRefsRef.current}
          selectedFace={selectedFace}
          showFaces={showFaces}
          showSpecialEffect={showSpecialEffect}
          selectedEffect={selectedEffect}
          calculateFaceStyle={calculateFaceStyle}
        />
      </div>

      {/* Navigation buttons */}
      <div className="minimove-bodygallery-buttons">
        <button className="back-button" onClick={handleBack}>
          חזרה
        </button>
        <button
          className="finish-button"
          onClick={handleFinish}
          disabled={images.length === 0}
        >
          אהבתי
        </button>
      </div>
    </div>
  );
};

export default MinimoveBodyGalleryPage;
